// Admin section layout — persistent sub-navigation for /admin/*.
//
// The admin pages (Stories 1.27–1.32, 2.x) were all built + route-guarded
// but had NO navigation between them: the app shell links only to
// /admin/applications, leaving every other admin surface reachable only by
// hand-typing URLs. This layout renders a collapsible VERTICAL sidebar +
// <Outlet/> for the nested admin routes (App.tsx), so an admin can move
// across the whole console. Mounted under a single <AdminGuard> at the
// /admin layout route.
//
// Interaction model (design-review rework — was a horizontal side-scrolling
// tab strip):
//   * Desktop (>= lg): the sidebar is a persistent, in-flow left column
//     (push layout) — expanded by default. The burger collapses it, and
//     content reclaims the width. The column is sticky under the app bar and
//     scrolls vertically on its own when the item list outgrows the viewport.
//   * Narrow (< lg): the sidebar defaults collapsed. The burger opens it as
//     an overlay drawer with a scrim; selecting an item (or the scrim) closes
//     it again. Content never reflows on narrow.
//
// Admin design language: density over ceremony — no hover motion beyond
// color/opacity, --gh-rhythm-admin (16px) for the vertical stack rhythm.

import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useAdminOpenApplicationCounts } from "../features/admin/useAdminOpenApplicationCounts";
import { useMatchMedia } from "../lib/useMatchMedia";

type AdminNavItem = { to: string; label: string };

// NAV LAW (design-system 13-patterns): exact route match only, one active
// item per nav, ever. Every NavLink below is rendered with `end`, so a
// parent route (e.g. "/admin/metrics") never stays lit on a deeper
// sub-path (the doubled-highlight bug) — prefix matching is banned. In the
// vertical idiom the active indicator is an INSET LEFT rule (2px gold), the
// direct analogue of the old inset-bottom rule; NavLink stamps
// aria-current="page" on the single active item.
const ADMIN_NAV: AdminNavItem[] = [
  { to: "/admin/users", label: "Users" },
  { to: "/admin/applications", label: "Creators" },
  { to: "/admin/portfolio-applications", label: "Portfolio applications" },
  { to: "/admin/portfolios", label: "Portfolios" },
  { to: "/admin/drops", label: "Shop" },
  { to: "/admin/feed/editorial", label: "Feed editorial" },
  { to: "/admin/bulletin", label: "Bulletin" },
  { to: "/admin/moderation", label: "Moderation" },
  { to: "/admin/metrics", label: "Metrics" },
  { to: "/admin/financial-tools", label: "Financial tools" },
  { to: "/admin/ledger-export", label: "Ledger export" },
  { to: "/admin/finance/close", label: "Period close" },
  // Story 11.11 — the country VAT register. Sits beside the finance surfaces
  // because that is what it governs: which rule a top-up is charged under.
  { to: "/admin/vat-register", label: "VAT register" },
];

export function AdminLayout() {
  const isDesktop = useMatchMedia("(min-width: 1024px)");
  // `null` = untouched → follow the viewport default (desktop open, narrow
  // collapsed). Once the admin toggles the burger, their choice sticks.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? isDesktop;

  // Pending-application counts rendered as "(N)" beside the two review
  // queues. Null (loading / endpoint hiccup) or 0 renders no badge.
  const counts = useAdminOpenApplicationCounts();
  const badgeByRoute: Record<string, number | null> = {
    "/admin/applications": counts.creatorApplications,
    "/admin/portfolio-applications": counts.portfolioApplications,
  };

  return (
    <div className="bg-brand-forest-900 text-brand-paper-100">
      {/* Sub-header strip — the burger toggle + section eyebrow. Full-bleed
          border, inner content aligned to the app bar's max-w-6xl. */}
      <div className="border-b border-brand-gold-500/20 bg-brand-forest-900/95">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-6 py-2">
          <button
            type="button"
            onClick={() => setUserOpen(!open)}
            aria-expanded={open}
            aria-controls="admin-sidebar-nav"
            aria-label={open ? "Collapse admin menu" : "Expand admin menu"}
            data-testid="admin-nav-toggle"
            className="flex h-8 w-8 items-center justify-center rounded text-brand-paper-100/80 transition-colors hover:bg-brand-forest-800/40 hover:text-brand-paper-100"
          >
            <BurgerIcon />
          </button>
          <span className="font-heading text-xs uppercase tracking-wide text-brand-gold-300/80">
            Admin
          </span>
        </div>
      </div>

      <div className="relative mx-auto flex w-full max-w-6xl">
        {open ? (
          <>
            {/* Narrow-only scrim behind the overlay drawer. */}
            {!isDesktop ? (
              <div
                className="fixed inset-0 z-30 bg-black/50"
                aria-hidden="true"
                onClick={() => setUserOpen(false)}
              />
            ) : null}

            <aside
              className={
                isDesktop
                  ? // Desktop: in-flow column, sticky under the 56px app bar,
                    // self-scrolling when the list is long.
                    "sticky top-14 z-0 max-h-[calc(100vh-3.5rem)] w-60 shrink-0 self-start overflow-y-auto border-r border-brand-gold-500/20 px-3 py-4"
                  : // Narrow: fixed overlay drawer above the scrim.
                    "fixed left-0 top-0 z-40 h-full w-64 overflow-y-auto border-r border-brand-gold-500/20 bg-brand-forest-900 px-3 py-4"
              }
            >
              <nav
                id="admin-sidebar-nav"
                aria-label="Admin sections"
                className="flex flex-col gap-[var(--gh-rhythm-admin,16px)]"
              >
                {ADMIN_NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end
                    onClick={() => {
                      // On narrow, picking a destination closes the drawer.
                      if (!isDesktop) setUserOpen(false);
                    }}
                    className={({ isActive }) =>
                      [
                        "block rounded px-3 py-1.5 font-body text-sm transition-colors",
                        // Active = inset LEFT 2px indicator + weight, not a
                        // full background swap. Hover = ghost fill only, no
                        // indicator, no motion beyond color.
                        isActive
                          ? "font-semibold text-brand-gold-300 shadow-[inset_2px_0_0_0_#cea33d]"
                          : "text-brand-paper-100/70 hover:bg-brand-forest-800/40 hover:text-brand-paper-100",
                      ].join(" ")
                    }
                  >
                    {item.label}
                    {(badgeByRoute[item.to] ?? 0) > 0 ? (
                      <span className="ml-1.5 font-mono text-xs text-brand-gold-300">
                        ({badgeByRoute[item.to]})
                      </span>
                    ) : null}
                  </NavLink>
                ))}
              </nav>
            </aside>
          </>
        ) : null}

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function BurgerIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}
